/**
 * Money is ALWAYS an integer number of halalas (1 SAR = 100 halalas).
 *
 * Never a float, never a string, never a decimal library — JavaScript integers
 * are exact to 2^53, and the largest figure in this portfolio is ~41.6M SAR
 * (4.16e9 halalas), which is eight orders of magnitude inside that. Column
 * names carry the unit (`amount_halalas`) so a float can never be assigned to
 * one by accident.
 */

/** Largest representable amount: 1 trillion SAR. Anything beyond is a typo. */
export const MAX_HALALAS = 100_000_000_000_000;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

/**
 * Parse a user-entered SAR amount into halalas.
 * Accepts "1234.56", "1,234.56", "١٢٣٤.٥٦", 1234.56. Rejects negatives,
 * non-finite values and anything with more than two decimal places — silently
 * rounding a third decimal is how reconciliation breaks.
 */
export function sarToHalalas(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value)
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[,\s\u066C\u00A0]/g, '') // comma, any space, Arabic thousands separator, NBSP
    .replace(/[٫]/g, '.');
  if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
    throw new MoneyError('المبلغ يجب أن يكون رقمًا موجبًا بحد أقصى منزلتين عشريتين');
  }
  const [whole = '0', frac = ''] = raw.split('.');
  const halalas = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(halalas) || halalas > MAX_HALALAS) {
    throw new MoneyError('المبلغ خارج النطاق المسموح');
  }
  return halalas;
}

/** Validate an amount that is already in halalas (from an API boundary). */
export function assertHalalas(value: unknown, field = 'amount'): number {
  if (!Number.isInteger(value) || (value as number) < 0 || (value as number) > MAX_HALALAS) {
    throw new MoneyError(`${field}: المبلغ يجب أن يكون عددًا صحيحًا من الهللات وغير سالب`);
  }
  return value as number;
}

/** Format halalas for display, e.g. 15180000 → "151,800.00". */
export function formatHalalas(halalas: number | null | undefined, withCurrency = false): string {
  if (halalas === null || halalas === undefined) return '—';
  const sign = halalas < 0 ? '-' : '';
  const abs = Math.abs(halalas);
  const body = `${sign}${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
  return withCurrency ? `${body} ر.س` : body;
}

/**
 * Split an amount into `n` instalments that sum EXACTLY to the original.
 * The remainder halalas go to the earliest instalments, which is the
 * conventional treatment and the only one that keeps the schedule tying out.
 * Legacy contract ctr-001 is precisely this case: 132,000 over 3 unequal
 * instalments (40,000 + 40,000 + 52,000) that the source could not explain.
 */
export function splitInstalments(totalHalalas: number, count: number): number[] {
  assertHalalas(totalHalalas, 'total');
  if (!Number.isInteger(count) || count < 1 || count > 366) {
    throw new MoneyError('عدد الأقساط غير صالح');
  }
  const base = Math.floor(totalHalalas / count);
  const remainder = totalHalalas - base * count;
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
}

/** Saudi standard VAT rate, as a percentage. Stored per-charge, not assumed. */
export const KSA_VAT_RATE = 15;

/** VAT on a net amount, rounded half-up to the halala (ZATCA convention). */
export function vatOn(netHalalas: number, ratePercent: number = KSA_VAT_RATE): number {
  assertHalalas(netHalalas, 'net');
  return Math.round((netHalalas * ratePercent) / 100);
}
